// importCsvUtils.js

import Papa from 'papaparse';
import { normalizeEmailValueForStorage } from 'utilities/Email';
import { normalizeEnumOptionValues } from 'utilities/EnumOptions';
import { normalizePhoneValueForStorage } from 'utilities/Phone';
import { createRichTextValue } from 'utilities/RichText';
import {
  assembleImportedLocation,
  expandLocationFieldColumns,
  isLocationImportField,
  listLocationImportFields,
  parseLocationPartTarget,
  resolveLocationPartMapping,
  shouldPrefixLocationPartHeaders,
} from './importLocationCsvUtils';

export const MAX_IMPORT_ROWS = 10000;
export const IMPORT_TARGET_TITLE = '__title__';
export const IMPORT_TARGET_IGNORE = '__ignore__';

const SUPPORTED_FIELD_TYPES = new Set([
  'string',
  'richtext',
  'phone',
  'email',
  'text',
  'textarea',
  'number',
  'percentage',
  'boolean',
  'enum',
  'url',
  'date',
  'datetime',
  'location',
]);

const TRUTHY_VALUES = new Set(['true', '1', 'yes', 'y']);
const FALSY_VALUES = new Set(['false', '0', 'no', 'n']);
const DEFAULT_TITLE_ALIASES = ['Title', 'Name', 'Item Title'];

const normalizeToken = (value) => String(value || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');
const stripKeyHint = (value) => String(value || '').replace(/\s*\([^)]*\)\s*\d*\s*$/, '').trim();

const toSectionKey = (fieldDef, typeDef, getType) => {
  const sourceTypeKey = String(fieldDef?.source || typeDef?.key || '').trim();
  if (!sourceTypeKey) {
    return String(typeDef?.dataSection || '').trim() || 'data_node';
  }

  const sourceDef = typeof getType === 'function' ? getType(sourceTypeKey) : null;
  if (sourceDef?.dataSection && String(sourceDef.dataSection).trim()) {
    return String(sourceDef.dataSection).trim();
  }

  return `data_${sourceTypeKey.toLowerCase()}`;
};

const buildTemplateExampleValue = (field) => {
  const type = String(field?.type || '').trim().toLowerCase();

  if (type === 'boolean') return 'Yes';
  if (type === 'number') return '123';
  if (type === 'percentage') return '50';
  if (type === 'date') return '2026-01-15';
  if (type === 'datetime') return '2026-01-15 09:00';
  if (type === 'url') return 'https://example.com';
  if (type === 'phone') return '(415) 555-1212';
  if (type === 'email') return 'name@example.com';

  if (type === 'enum') {
    const optionValues = normalizeEnumOptionValues(field?.options || []);
    return optionValues[0] || '';
  }

  return '';
};

export const getFieldKey = (fieldDef) => String(fieldDef?.name || fieldDef?.key || '').trim();

export const isSupportedImportType = (fieldType) => SUPPORTED_FIELD_TYPES.has(String(fieldType || '').trim().toLowerCase());

export function buildImportableFields({ resolvedProperties = [], typeDef = null, getType = null } = {}) {
  const fields = Array.isArray(resolvedProperties) ? resolvedProperties : [];
  const byKey = new Map();

  fields.forEach((fieldDef) => {
    const key = getFieldKey(fieldDef);
    if (!key || byKey.has(key)) return;

    const normalizedType = String(fieldDef?.type || '').trim().toLowerCase();
    const supported = isSupportedImportType(normalizedType);

    byKey.set(key, {
      key,
      label: String(fieldDef?.label || key),
      type: normalizedType,
      supported,
      sectionKey: toSectionKey(fieldDef, typeDef, getType),
      source: String(fieldDef?.source || typeDef?.key || ''),
      options: Array.isArray(fieldDef?.options) ? fieldDef.options : [],
    });
  });

  return Array.from(byKey.values());
}

const uniqueTemplateHeader = (baseHeader, usedTokens, fieldKey = '', partId = '') => {
  const fallbackKey = [fieldKey, partId].filter(Boolean).join(' ');
  let nextHeader = String(baseHeader || '').trim() || fallbackKey || 'Field';
  let nextToken = normalizeToken(nextHeader);

  if (!nextToken || usedTokens.has(nextToken)) {
    nextHeader = fallbackKey ? `${nextHeader} (${fallbackKey})` : nextHeader;
    nextToken = normalizeToken(nextHeader);
  }

  let bump = 2;
  while (!nextToken || usedTokens.has(nextToken)) {
    nextHeader = `${baseHeader} (${fallbackKey || 'field'}) ${bump}`;
    nextToken = normalizeToken(nextHeader);
    bump += 1;
  }

  usedTokens.add(nextToken);
  return nextHeader;
};

export function buildImportTemplateCsv({
  fields = [],
  singularLabel = 'Item',
  titleFieldLabel = 'Title',
} = {}) {
  const supportedFields = (Array.isArray(fields) ? fields : []).filter((field) => field?.supported);
  const prefixLocationParts = shouldPrefixLocationPartHeaders(supportedFields);
  const locationFields = listLocationImportFields(supportedFields);
  const locationLabelCounts = new Map();
  locationFields.forEach((field) => {
    const token = normalizeToken(field?.label || field?.key);
    if (!token) return;
    locationLabelCounts.set(token, (locationLabelCounts.get(token) || 0) + 1);
  });

  const usedTokens = new Set();
  const titleHeader = uniqueTemplateHeader(
    String(titleFieldLabel || 'Title').trim() || 'Title',
    usedTokens,
    'title',
  );
  const headers = [titleHeader];
  const exampleRow = [`Example ${String(singularLabel || 'Item').trim() || 'Item'}`];

  supportedFields.forEach((field) => {
    if (isLocationImportField(field)) {
      const labelToken = normalizeToken(field?.label || field?.key);
      const disambiguateKey = (locationLabelCounts.get(labelToken) || 0) > 1;
      expandLocationFieldColumns(field, {
        prefix: prefixLocationParts,
        disambiguateKey,
      }).forEach((column) => {
        headers.push(uniqueTemplateHeader(
          column.header,
          usedTokens,
          field.key,
          column.locationPart,
        ));
        exampleRow.push(column.example || '');
      });
      return;
    }

    headers.push(uniqueTemplateHeader(
      String(field?.label || field?.key || '').trim(),
      usedTokens,
      field?.key,
    ));
    exampleRow.push(buildTemplateExampleValue(field));
  });

  return Papa.unparse({
    fields: headers,
    data: [exampleRow],
  });
}

export async function parseCsvFile(file) {
  if (!file) {
    return { ok: false, error: 'Select a CSV file.' };
  }

  const fileName = String(file.name || '').toLowerCase();
  if (!fileName.endsWith('.csv')) {
    return { ok: false, error: 'Only .csv files are supported.' };
  }

  const text = await file.text();
  const normalizedText = String(text || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n');
  const parsed = Papa.parse(normalizedText, {
    header: true,
    skipEmptyLines: 'greedy',
    transformHeader: (header) => String(header || '').trim(),
  });

  if (Array.isArray(parsed.errors) && parsed.errors.length > 0) {
    const first = parsed.errors[0];
    return {
      ok: false,
      error: first?.message || 'Failed to parse CSV file.',
    };
  }

  const headers = Array.isArray(parsed.meta?.fields)
    ? parsed.meta.fields.map((header) => String(header || '').trim()).filter(Boolean)
    : [];

  if (!headers.length) {
    return { ok: false, error: 'CSV header row is required.' };
  }

  const hasDuplicateHeaders = headers.length !== new Set(headers).size;
  if (hasDuplicateHeaders) {
    return { ok: false, error: 'Duplicate CSV headers detected. Headers must be unique.' };
  }

  const rows = Array.isArray(parsed.data) ? parsed.data : [];
  if (!rows.length) {
    return { ok: false, error: 'CSV has no data rows.' };
  }

  if (rows.length > MAX_IMPORT_ROWS) {
    return {
      ok: false,
      error: `CSV has ${rows.length} rows. The limit is ${MAX_IMPORT_ROWS}.`,
    };
  }

  return {
    ok: true,
    headers,
    rows,
  };
}

export function buildInitialMappings({ headers = [], fields = [], titleFieldLabel = 'Title' } = {}) {
  const supportedFields = (Array.isArray(fields) ? fields : []).filter((field) => field.supported);
  const scalarFields = supportedFields.filter((field) => !isLocationImportField(field));
  const byExactKey = new Map();
  const byNormalizedKey = new Map();
  const byNormalizedLabel = new Map();
  const resolvedTitleLabel = String(titleFieldLabel || '').trim();
  const titleAliases = new Set(
    [
      ...DEFAULT_TITLE_ALIASES,
      resolvedTitleLabel,
      resolvedTitleLabel ? `${resolvedTitleLabel} Name` : '',
    ]
      .filter(Boolean)
      .map((value) => normalizeToken(value))
  );

  scalarFields.forEach((field) => {
    byExactKey.set(field.key, field.key);
    byNormalizedKey.set(normalizeToken(field.key), field.key);
    byNormalizedLabel.set(normalizeToken(field.label), field.key);
  });

  const mappings = {};

  (Array.isArray(headers) ? headers : []).forEach((header) => {
    const normalizedHeader = normalizeToken(header);
    const simplifiedHeader = stripKeyHint(header);
    const normalizedSimplifiedHeader = normalizeToken(simplifiedHeader);

    if (titleAliases.has(normalizedHeader) || titleAliases.has(normalizedSimplifiedHeader)) {
      mappings[header] = IMPORT_TARGET_TITLE;
      return;
    }

    if (byExactKey.has(header)) {
      mappings[header] = byExactKey.get(header);
      return;
    }

    if (byNormalizedKey.has(normalizedHeader)) {
      mappings[header] = byNormalizedKey.get(normalizedHeader);
      return;
    }

    if (byNormalizedKey.has(normalizedSimplifiedHeader)) {
      mappings[header] = byNormalizedKey.get(normalizedSimplifiedHeader);
      return;
    }

    if (byNormalizedLabel.has(normalizedHeader)) {
      mappings[header] = byNormalizedLabel.get(normalizedHeader);
      return;
    }

    if (byNormalizedLabel.has(normalizedSimplifiedHeader)) {
      mappings[header] = byNormalizedLabel.get(normalizedSimplifiedHeader);
      return;
    }

    const locationTarget = resolveLocationPartMapping(header, supportedFields);
    if (locationTarget) {
      mappings[header] = locationTarget;
      return;
    }

    mappings[header] = IMPORT_TARGET_IGNORE;
  });

  return mappings;
}

const parseDateValue = (rawValue) => {
  const value = String(rawValue || '').trim();
  if (!value) return { ok: true, value: null };

  const asDate = new Date(value);
  if (Number.isNaN(asDate.getTime())) {
    return { ok: false, reason: `Invalid date value "${value}".` };
  }

  return { ok: true, value: asDate.toISOString() };
};

const parseFieldValue = (field, rawValue) => {
  const value = String(rawValue ?? '').trim();
  if (value === '') {
    return { ok: true, empty: true };
  }

  const type = String(field?.type || '').toLowerCase();

  if (type === 'string' || type === 'text' || type === 'textarea') {
    return { ok: true, value };
  }

  if (type === 'url') {
    return {
      ok: true,
      value: {
        url: value,
        indexContent: false,
        fetchStatus: 'idle',
        title: null,
        summary: null,
        canonicalUrl: null,
        fetchedAt: null,
        fetchError: null,
        extractId: null,
        lastFetchedUrl: null,
      },
    };
  }

  if (type === 'richtext') {
    return { ok: true, value: createRichTextValue(value) };
  }

  if (type === 'phone') {
    return { ok: true, value: normalizePhoneValueForStorage(value) };
  }

  if (type === 'email') {
    return { ok: true, value: normalizeEmailValueForStorage(value) };
  }

  if (type === 'number') {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
      return { ok: false, reason: `Expected number for ${field.label}.` };
    }
    return { ok: true, value: numeric };
  }

  if (type === 'percentage') {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
      return { ok: false, reason: `Expected percentage for ${field.label}.` };
    }
    const clamped = Math.max(0, Math.min(100, Math.round(numeric)));
    return { ok: true, value: clamped };
  }

  if (type === 'boolean') {
    const normalized = value.toLowerCase();
    if (TRUTHY_VALUES.has(normalized)) return { ok: true, value: true };
    if (FALSY_VALUES.has(normalized)) return { ok: true, value: false };
    return { ok: false, reason: `Expected boolean for ${field.label} (true/false, yes/no, 1/0).` };
  }

  if (type === 'enum') {
    const optionValues = normalizeEnumOptionValues(field?.options || []);
    const directMatch = optionValues.find((candidate) => candidate === value);
    if (directMatch) {
      return { ok: true, value: directMatch };
    }

    const lowered = value.toLowerCase();
    const fuzzyMatch = optionValues.find((candidate) => String(candidate).toLowerCase() === lowered);
    if (fuzzyMatch) {
      return { ok: true, value: fuzzyMatch };
    }

    return { ok: false, reason: `Expected one of [${optionValues.join(', ')}] for ${field.label}.` };
  }

  if (type === 'date' || type === 'datetime') {
    return parseDateValue(value);
  }

  if (type === 'location') {
    const assembled = assembleImportedLocation({ full: value });
    if (!assembled) return { ok: true, empty: true };
    return { ok: true, value: assembled };
  }

  return { ok: false, reason: `${field.label} is not supported for import.` };
};

export function buildImportPreview({
  rows = [],
  headers = [],
  mappings = {},
  fieldsByKey = new Map(),
  titleFieldLabel = 'Title',
} = {}) {
  const normalizedRows = Array.isArray(rows) ? rows : [];
  const normalizedHeaders = Array.isArray(headers) ? headers : [];

  const validRows = [];
  const skippedRows = [];
  const previewRows = [];

  normalizedRows.forEach((row, index) => {
    const rowNumber = index + 2;
    const rowErrors = [];
    const sectionData = {};
    const locationPartsByField = new Map();
    let title = '';

    const writeSectionValue = (field, value) => {
      const sectionKey = field.sectionKey || 'data_node';
      if (!sectionData[sectionKey]) sectionData[sectionKey] = {};
      sectionData[sectionKey][field.key] = value;
    };

    normalizedHeaders.forEach((header) => {
      const target = mappings?.[header] || IMPORT_TARGET_IGNORE;
      const rawValue = row?.[header];

      if (target === IMPORT_TARGET_IGNORE) return;

      if (target === IMPORT_TARGET_TITLE) {
        const titleValue = String(rawValue ?? '').trim();
        if (titleValue) title = titleValue;
        return;
      }

      const locationTarget = parseLocationPartTarget(target);
      if (locationTarget) {
        const field = fieldsByKey.get(locationTarget.fieldKey);
        if (!field || !field.supported || !isLocationImportField(field)) return;

        const partValue = String(rawValue ?? '').trim();
        if (!partValue) return;

        const current = locationPartsByField.get(field.key) || { field, parts: {} };
        current.parts[locationTarget.part] = partValue;
        locationPartsByField.set(field.key, current);
        return;
      }

      const field = fieldsByKey.get(target);
      if (!field || !field.supported) return;

      const parsed = parseFieldValue(field, rawValue);
      if (!parsed.ok) {
        rowErrors.push(parsed.reason || `Invalid value for ${field.label}.`);
        return;
      }

      if (parsed.empty) return;

      writeSectionValue(field, parsed.value);
    });

    locationPartsByField.forEach(({ field, parts }) => {
      const assembled = assembleImportedLocation(parts);
      if (!assembled) return;
      writeSectionValue(field, assembled);
    });

    if (!title) {
      rowErrors.push(`${titleFieldLabel} is required. Fill in that column for each row.`);
    }

    const parsedRow = {
      rowNumber,
      title,
      sectionData,
      errors: rowErrors,
      isValid: rowErrors.length === 0,
    };

    previewRows.push(parsedRow);

    if (rowErrors.length === 0) {
      validRows.push(parsedRow);
    } else {
      skippedRows.push(parsedRow);
    }
  });

  return {
    previewRows,
    validRows,
    skippedRows,
  };
}

/**
 * Normalizes a title for import de-dupe (case/whitespace insensitive).
 *
 * @param {*} value
 * @returns {string}
 */
export function normalizeImportTitle(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[.,;:!?]+$/g, '')
    .trim();
}

/**
 * Builds a set of normalized titles from existing nodes.
 *
 * @param {Array<object>} nodes
 * @returns {Set<string>}
 */
export function buildExistingImportTitleSet(nodes = []) {
  const titles = new Set();
  (Array.isArray(nodes) ? nodes : []).forEach((node) => {
    const normalized = normalizeImportTitle(node?.title);
    if (normalized) titles.add(normalized);
  });
  return titles;
}

/**
 * Splits valid import rows into create vs skip-as-duplicate by normalized title.
 * Within the CSV, the first occurrence wins; later same titles are skipped too.
 *
 * @param {object} options
 * @param {Array<object>} options.validRows
 * @param {Set<string>|Iterable<string>} [options.existingTitles]
 * @returns {{ toImport: object[], skippedDuplicates: object[] }}
 */
export function partitionImportRowsByTitle({
  validRows = [],
  existingTitles = null,
} = {}) {
  const seen = new Set(
    existingTitles instanceof Set
      ? existingTitles
      : Array.isArray(existingTitles)
        ? existingTitles
        : [],
  );
  const toImport = [];
  const skippedDuplicates = [];

  (Array.isArray(validRows) ? validRows : []).forEach((row) => {
    const normalized = normalizeImportTitle(row?.title);
    if (!normalized) {
      toImport.push(row);
      return;
    }

    if (seen.has(normalized)) {
      skippedDuplicates.push(row);
      return;
    }

    seen.add(normalized);
    toImport.push(row);
  });

  return { toImport, skippedDuplicates };
}
